/**
 * useHomeReady - Coordinates all async home screen loading steps.
 *
 * Tracks multiple readiness signals and only declares "ready" when
 * ALL of them have completed (or timed out). This lets us show a
 * skeleton placeholder and then reveal the fully-loaded home in one go.
 *
 * Signals tracked:
 *  - auth:           background Pi authentication finished
 *  - tiles:          homescreen sections have at least 1 item
 *  - catalogSettled: app catalog loaded from server (after auth succeeds)
 *  - layoutSynced:   tile layout synced from server (after auth succeeds)
 *  - iconsReady:     visible tile icons preloaded into browser cache
 *
 * Reveal policy:
 *  - When a session token is cached at mount, server-side data (catalog +
 *    layout + icons) WILL load regardless of how auth phase progresses, so
 *    always wait for those signals. This prevents the "home reveals with
 *    stale tiles, then rearranges seconds later" symptom that occurs when
 *    auth is still in flight but the optimistic catalog/layout fetches
 *    haven't completed yet.
 *  - When there's no token and auth gives up (phase='idle'), no server data
 *    will load, so reveal as soon as we have tiles.
 *
 * Safety: a maximum timeout (default 8s) ensures the home always reveals
 * even if some signals never fire (e.g. offline, API down).
 */

import { useState, useEffect, useRef } from 'react';
import { useHomeSections } from '@room/shared-ui';
import { getSessionToken } from '@room/frontend-sdk';
import type { AuthPhase } from './useBackgroundAuth';

const MAX_WAIT_MS = 8000; // Absolute maximum wait before force-revealing

interface UseHomeReadyOptions {
  authPhase: AuthPhase;
  /** True once loadAppCatalog() has completed (success or failure) */
  catalogSettled: boolean;
  /** True once useLayoutSync has fetched server layout (or decided not to) */
  layoutSynced: boolean;
  /** True once visible tile icons have been preloaded into browser cache */
  iconsReady: boolean;
}

export function useHomeReady({ authPhase, catalogSettled, layoutSynced, iconsReady }: UseHomeReadyOptions): boolean {
  const [isReady, setIsReady] = useState(false);
  const sections = useHomeSections();
  const readyRef = useRef(false);

  // Snapshot whether a session token was present at mount time. If yes,
  // the optimistic catalog + layout fetches will run regardless of how
  // useBackgroundAuth phase progresses, and we must wait for them.
  const hasTokenAtMount = useRef(!!getSessionToken()).current;

  // Auth is considered "settled" when it's either done or gave up (idle).
  // Intermediate phases (checking/refreshing/etc.) still keep the gate closed.
  const authSettled = authPhase === 'done' || authPhase === 'idle';

  // Tiles are considered loaded when the main section has at least 1 item
  const mainSection = sections.find((s) => s.id === 'main');
  const hasTiles = (mainSection?.items.length ?? 0) > 0;

  // Wait for server data whenever it's actually expected to arrive.
  // Expected when: auth succeeded OR a cached token at mount triggered the
  // optimistic fetches. Skip the wait only when we definitively have no
  // authenticated session in flight (no token + auth gave up).
  const expectingServerData = authPhase === 'done' || hasTokenAtMount;
  const dataSettled = !expectingServerData || (catalogSettled && layoutSynced && iconsReady);

  useEffect(() => {
    if (readyRef.current) return;

    if (authSettled && hasTiles && dataSettled) {
      // Small delay to let React commit the final DOM batch
      const id = setTimeout(() => {
        readyRef.current = true;
        setIsReady(true);
      }, 120);
      return () => clearTimeout(id);
    }
  }, [authSettled, hasTiles, dataSettled]);

  // Safety timeout - always reveal after MAX_WAIT_MS
  useEffect(() => {
    if (readyRef.current) return;

    const id = setTimeout(() => {
      if (!readyRef.current) {
        readyRef.current = true;
        setIsReady(true);
      }
    }, MAX_WAIT_MS);

    return () => clearTimeout(id);
  }, []);

  return isReady;
}
